import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { toast } from "sonner";
import { ArrowRight, Loader2, Check } from "lucide-react";

const INDUSTRIES = [
  { v: "property", l: "Property Management" },
  { v: "hr", l: "Recruitment / HR" },
  { v: "vendor", l: "Vendor / Supplier" },
  { v: "services", l: "Professional Services" },
  { v: "general", l: "Other" },
];

export default function Signup() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ org_name: "", industry: "property", admin_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/orgs/register", f);
      login(data.token, data.user);
      toast.success("Workspace created — welcome!");
      nav("/app");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const input = "mt-1.5 w-full border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background";

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative bg-primary noise-bg flex-col justify-center px-16 text-primary-foreground order-2 lg:order-1">
        <div className="absolute inset-0 grid-lines opacity-20" aria-hidden="true" />
        <div className="relative">
          <h2 className="text-5xl font-light tracking-tighter leading-tight">Start your<br /><span className="font-black">14-day free trial.</span></h2>
          <ul className="mt-10 space-y-4">
            {["No credit card required", "Ready-made industry templates", "Unlimited team members on paid plans", "Cancel anytime"].map((x) => (
              <li key={x} className="flex items-center gap-3 text-primary-foreground/80"><Check className="h-5 w-5 text-accent" /> {x}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className="flex flex-col justify-center px-8 sm:px-16 py-12 order-1 lg:order-2">
        <Brand className="mb-10" />
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
          <p className="overline text-accent mb-3">Create your organization</p>
          <h1 className="text-4xl font-light tracking-tighter mb-8">Set up your workspace</h1>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Organization name</label>
              <input required value={f.org_name} onChange={set("org_name")} data-testid="signup-org-input" className={input} placeholder="Acme Lettings" />
            </div>
            <div>
              <label className="text-sm font-medium">Industry</label>
              <select value={f.industry} onChange={set("industry")} data-testid="signup-industry-select" className={input}>
                {INDUSTRIES.map((i) => <option key={i.v} value={i.v}>{i.l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Your name</label>
              <input required value={f.admin_name} onChange={set("admin_name")} data-testid="signup-name-input" className={input} placeholder="Jane Doe" />
            </div>
            <div>
              <label className="text-sm font-medium">Work email</label>
              <input type="email" required value={f.email} onChange={set("email")} data-testid="signup-email-input" className={input} placeholder="jane@acme.com" />
            </div>
            <div>
              <label className="text-sm font-medium">Password</label>
              <input type="password" required minLength={6} value={f.password} onChange={set("password")} data-testid="signup-password-input" className={input} placeholder="At least 6 characters" />
            </div>
            <button type="submit" disabled={loading} data-testid="signup-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold py-3 hover:bg-accent transition-colors disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create workspace <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>
          <p className="mt-6 text-sm text-muted-foreground">
            Already have an account? <Link to="/login" className="text-accent font-medium hover:underline" data-testid="signup-to-login-link">Sign in →</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
